import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import CommonLayout from '../../layouts/CommonLayout';
import OptionModal from '../../layouts/OptionModal';
import SimpleInfoModal from '../../layouts/SimpleInfoModal';
import StaticKakaoMapContainer from '../../containers/StaticKakaoMapContainer';
import MeetingStatusIndicator from '../../containers/MeetingStatusIndicator';
import { getMeetingById, deleteMeetingById, confirmMeeting } from '../../../api/MeetingApi';
import { deleteMemberOnMeeting, joinMemberOnMeeting } from '../../../api/MemberMeetingApi';
import { getPartnerPlacePolicy } from '../../../api/PartnerPlaceApi';
import useLoadingStore from '../../../store/loadingStore';
import useAuthStore from '../../../store/authStore';
import { IoCalendarOutline, IoTimeOutline, IoPeopleOutline, IoRestaurantOutline, IoPersonOutline } from 'react-icons/io5';
import PointContainer from '../../containers/PointContainer';
import { AlertModal } from '../../layouts/AlertModal';
import { ConfirmModal } from '../../layouts/ConfirmModal';
import { block as blockMember } from '../../../api/BlockApi';

const MeetingPage = () => {
    const { meetingId } = useParams();
    const navigate = useNavigate();
    const location = useLocation();
    const { setIsLoading } = useLoadingStore();
    const { member } = useAuthStore();
    const [meetingData, setMeetingData] = useState(null);
    const [showSettingsModal, setShowSettingsModal] = useState(false);
    const [showPolicyModal, setShowPolicyModal] = useState(false);
    const [partnerPlacePolicy, setPartnerPlacePolicy] = useState('');
    const [policyLoading, setPolicyLoading] = useState(false);

    // 모달 상태 관리
    const [alertState, setAlertState] = useState({ isVisible: false, message: '', callback: null });
    const [confirmState, setConfirmState] = useState({ isVisible: false, title: '', message: '', onConfirm: () => {} });

    // 헬퍼 함수
    const showConfirm = (title, message, onConfirmAction) => {
        setConfirmState({
            isVisible: true,
            title,
            message,
            onConfirm: () => {
                onConfirmAction();
                setConfirmState({ ...confirmState, isVisible: false });
            }
        });
    };

    const getErrorMessage = (error) => {
        if (error.response && error.response.data && error.response.data.message) {
            return error.response.data.message;
        }
        return '오류가 발생했습니다. 잠시 후 다시 시도해주세요.';
    };

    const isFromChat = location.state?.fromChat || document.referrer.includes('/chat/');

    useEffect(() => {
        loadMeetingData();
    }, [meetingId]);

    const loadMeetingData = async () => {
        try {
            setIsLoading(true);
            const response = await getMeetingById(meetingId);
            setMeetingData(response);
        } catch (error) {
            console.error('모임 정보 조회 실패:', error);
            setAlertState({ isVisible: true, message: '모임 정보를 불러오는 중 오류가 발생했습니다.' });
        } finally {
            setIsLoading(false);
        }
    };

    const formatDate = (scheduledAt) => {
        const date = new Date(scheduledAt);
        const year = date.getFullYear();
        const month = date.getMonth() + 1;
        const day = date.getDate();
        const weekdays = ['일', '월', '화', '수', '목', '금', '토'];
        const weekday = weekdays[date.getDay()];
        return `${year}년 ${month}월 ${day}일 (${weekday})`;
    };

    const formatTime = (scheduledAt) => {
        const date = new Date(scheduledAt);
        const hours = date.getHours();
        const minutes = date.getMinutes();
        const period = hours < 12 ? '오전' : '오후';
        const displayHours = hours === 0 ? 12 : hours > 12 ? hours - 12 : hours;
        return `${period} ${displayHours}:${minutes.toString().padStart(2, '0')}`;
    };

    const isHost = member && meetingData && member.id === meetingData.hostId;
    const isMember = member && meetingData && meetingData.members.some(m => m.id === member.id);

    const handleSettingsClick = () => {
        setShowSettingsModal(true);
    };

    const handleEditMeeting = () => {
        setShowSettingsModal(false);
        if (meetingData.status !== 'RECRUITING') {
            setAlertState({ isVisible: true, message: '모임이 확정된 경우에는 수정할 수 없습니다' });
            return;
        }
        navigate(`/app/meetings/${meetingId}/update`);
    };

    const handleDeleteMeeting = async () => {
        setShowSettingsModal(false);
        if (meetingData.memberCount > 1) {
            setAlertState({ isVisible: true, message: '모임원이 두명 이상이면 삭제할 수 없습니다' });
            return;
        }
        showConfirm('모임 삭제', '정말 모임을 삭제하시겠습니까? 쌀 500알이 환급됩니다.', async () => {
            try {
                setIsLoading(true);
                await deleteMeetingById(meetingId);
                setAlertState({
                    isVisible: true,
                    message: '모임이 삭제되었습니다.',
                    callback: () => navigate('/app/meetings', { replace: true })
                });
            } catch (error) {
                console.error('모임 삭제 실패:', error);
                setAlertState({ isVisible: true, message: getErrorMessage(error) });
            } finally {
                setIsLoading(false);
            }
        });
    };

    const handleLeaveMeeting = async () => {
        setShowSettingsModal(false);
        const confirmMessage = (() => {
            // 모임장인 경우
            if (isHost) {
                // 모임장 + 모집 중
                if (meetingData.status === 'RECRUITING') {
                    return '정말 모임에서 나가시겠습니까?\n모임장 권한은 가장 먼저 들어온 모임원에게 위임됩니다.\n쌀 500알이 환급됩니다.';
                }
                // 모임장 + 그외 상태
                return '정말 모임에서 나가시겠습니까?\n모임장 권한은 가장 먼저 들어온 모임원에게 위임됩니다.';
            }

            // 모임원이면서 모집 중
            if (meetingData.status === 'RECRUITING') {
                return '정말 모임에서 나가시겠습니까?\n쌀 500알이 환급됩니다.';
            }

            // else
            return '정말 모임에서 나가시겠습니까?';
        })();


        showConfirm('모임 나가기', confirmMessage, async () => {
            try {
                setIsLoading(true);
                await deleteMemberOnMeeting(meetingId);
                setAlertState({
                    isVisible: true,
                    message: '모임에서 나갔습니다.',
                    callback: () => navigate('/app/meetings', { replace: true })
                });
            } catch (error) {
                console.error('모임 나가기 실패:', error);
                setAlertState({ isVisible: true, message: getErrorMessage(error) });
            } finally {
                setIsLoading(false);
            }
        });
    };

    const getModalOptions = () => {
        const options = [];
        if (!isMember) {
            options.push({ label: '신고하기', onClick: () => navigate(`/app/meetings/${meetingId}/report/out-of-meeting`, { state: { reportedId: meetingData.hostId } }), destructive: true });
            options.push({ label: '차단하기', onClick: async () => {
                setShowSettingsModal(false);
                setShowPolicyModal(false);
                showConfirm(
                    '차단하기',
                    '해당 밥친구를 차단하시겠습니까? 해당 밥친구의 모든 채팅과 모임이 보이지 않게 됩니다',
                    async () => {
                        try {
                            setIsLoading(true);
                            await blockMember(meetingData.hostId);
                            setAlertState({ isVisible: true, message: '해당 모임의 모임장인 밥친구가 차단되었습니다.' });
                        } catch (error) {
                            setAlertState({ isVisible: true, message: getErrorMessage(error) });
                        } finally {
                            setIsLoading(false);
                        }
                    }
                );
            }});
            return options;
        }
        if (isHost) {
            options.push({ label: '모임 수정', onClick: handleEditMeeting });
            if (meetingData.memberCount > 1) {
                options.push({ label: '모임 나가기', onClick: handleLeaveMeeting, destructive: true });
            } else {
                options.push({ label: '모임 삭제', onClick: handleDeleteMeeting, destructive: true });
            }
        } else {
            options.push({ label: '모임 나가기', onClick: handleLeaveMeeting, destructive: true });
        }
        return options;
    };

    const handleActionButtonClick = async () => {
        if (isMember) {
            navigate(`/app/chat/${meetingId}`, { replace: true });
        } else if (meetingData.requiresApproval && !meetingData.isApplicationSubmitted) {
            navigate(`/app/meetings/${meetingId}/application`);
        } else if (!meetingData.requiresApproval) {
            showConfirm('바로 참가', `모임에 바로 참가하시겠습니까?\n 쌀 500알이 차감됩니다.`, async () => {
                try {
                    setIsLoading(true);
                    await joinMemberOnMeeting(meetingId);
                    setAlertState({
                        isVisible: true,
                        message: '모임에 참가했습니다.',
                        callback: () => navigate('/app/meetings', { replace: true })
                    });
                } catch (error) {
                    setAlertState({ isVisible: true, message: getErrorMessage(error) });
                } finally {
                    setIsLoading(false);
                }
            });
        }
    };

    const handleApplicationManagement = () => {
        navigate(`/app/meetings/${meetingId}/applications`);
    };

    const handleConfirmMeeting = async () => {
        showConfirm('모임 확정', '모임을 확정하시겠습니까? 확정 후에는 취소할 수 없습니다.', async () => {
            try {
                setIsLoading(true);
                await confirmMeeting(meetingId);
                setAlertState({
                    isVisible: true,
                    message: '모임이 확정되었습니다.',
                    callback: () => loadMeetingData()
                });
            } catch (error) {
                console.error('모임 확정 실패:', error);
                setAlertState({ isVisible: true, message: getErrorMessage(error) });
            } finally {
                setIsLoading(false);
            }
        });
    };

    const handlePartnerPlaceBenefitClick = async () => {
        if (!meetingData.partnerPlaceId) return;
        try {
            setPolicyLoading(true);
            setShowPolicyModal(true);
            const response = await getPartnerPlacePolicy(meetingData.partnerPlaceId);
            setPartnerPlacePolicy(response.policy || '제휴 혜택 정보가 없습니다.');
        } catch (error) {
            console.error('제휴매장 정책 조회 실패:', error);
            setPartnerPlacePolicy('제휴 혜택 정보를 불러오는데 실패했습니다.');
        } finally {
            setPolicyLoading(false);
        }
    };

    const handleClosePolicyModal = () => {
        setShowPolicyModal(false);
        setPartnerPlacePolicy('');
    };

    const showApplicationManagement = isHost && meetingData?.requiresApproval;
    const showConfirmButton = isHost && meetingData?.status === 'RECRUITING';
    const canConfirmMeeting = showConfirmButton && meetingData?.memberCount >= meetingData?.minCapacity;

    if (!meetingData) {
        return (
            <CommonLayout title="모임 정보">
                <AlertModal
                    isVisible={alertState.isVisible}
                    message={alertState.message}
                    callback={alertState.callback}
                    onClose={() => setAlertState({ isVisible: false, message: '', callback: null })}
                />
                <div className="flex items-center justify-center h-64">
                    <p className="text-gray-500">모임 정보를 불러오는 중...</p>
                </div>
            </CommonLayout>
        );
    }

    return (
        <CommonLayout
            title="모임 정보"
            showMoreButton={true}
            onMore={handleSettingsClick}
        >
            <AlertModal
                isVisible={alertState.isVisible}
                message={alertState.message}
                callback={alertState.callback}
                onClose={() => setAlertState({ isVisible: false, message: '', callback: null })}
            />
            <ConfirmModal
                isVisible={confirmState.isVisible}
                title={confirmState.title}
                message={confirmState.message}
                onConfirm={confirmState.onConfirm}
                onCancel={() => setConfirmState({ ...confirmState, isVisible: false })}
            />
            <div className="max-w-md mx-auto bg-white">
                {/* 이미지 섹션 */}
                <div className="w-full h-60 bg-gray-100 relative overflow-hidden">
                    {meetingData.imageUrl ? (
                        <img
                            src={meetingData.imageUrl}
                            alt="모임 사진"
                            className="w-full h-full object-cover"
                        />
                    ) : (
                        <div className="w-full h-full flex items-center justify-center bg-gray-200">
                            <IoRestaurantOutline size={48} className="text-gray-400" />
                        </div>
                    )}
                </div>

                <div className="p-4 space-y-6">
                    {/* 제목과 상태 */}
                    <div className="flex items-start justify-between">
                        <h1 className="text-xl font-semibold text-gray-900 flex-1 pr-3">
                            {meetingData.title}
                        </h1>
                        <MeetingStatusIndicator status={meetingData.status} />
                    </div>

                    {/* 제휴매장 혜택 표시 */}
                    {meetingData.partnerPlaceId && (
                        <div className="relative -mt-2 mb-2">
                            <button
                                onClick={handlePartnerPlaceBenefitClick}
                                className="inline-flex items-center px-3 py-1.5 rounded-full border border-blue-200 shadow-sm active:shadow-md transition-shadow"
                                style={{
                                    background: 'linear-gradient(135deg, #001f3f 0%, #003d7a 20%, #0074D9 40%, #39CCCC 60%, #2ECC40 80%, #3D9970 100%)'
                                }}>
                                <div className="relative mr-2">
                                    <span className="text-white text-sm drop-shadow-sm">✦</span>
                                </div>
                                <span className="text-white text-sm font-medium drop-shadow-sm">
                                    제휴 매장 혜택
                                </span>
                            </button>
                        </div>
                    )}

                    {/* 설명 */}
                    <p className="text-gray-700 leading-relaxed">
                        {meetingData.content}
                    </p>

                    {/* 모임 일시 */}
                    <div>
                        <h3 className="text-lg font-medium text-gray-900 mb-4">모임 일시</h3>
                        <div className="space-y-3">
                            <div className="flex items-center">
                                <IoCalendarOutline size={20} className="text-gray-400 mr-3" />
                                <span className="text-gray-700">
                                    {formatDate(meetingData.scheduledAt)}
                                </span>
                            </div>
                            <div className="flex items-center">
                                <IoTimeOutline size={20} className="text-gray-400 mr-3" />
                                <span className="text-gray-700">
                                    {formatTime(meetingData.scheduledAt)}
                                </span>
                            </div>
                        </div>
                    </div>

                    {/* 모임 장소 */}
                    <div>
                        <h3 className="text-lg font-medium text-gray-900 mb-4">모임 장소</h3>
                        <div className="space-y-3">
                            {/* 지도 */}
                            <div className="w-full aspect-[2/1] bg-gray-100 rounded-lg overflow-hidden border border-gray-200">
                                <StaticKakaoMapContainer
                                    latitude={meetingData.placeLatitude}
                                    longitude={meetingData.placeLongitude}
                                    level={3}
                                    className="w-full h-full"
                                />
                            </div>

                            {/* 장소 정보 */}
                            <div
                                className="p-4 rounded-lg border"
                                style={meetingData.partnerPlaceId ? {
                                    background: 'linear-gradient(135deg, rgba(0,31,63,0.05) 0%, rgba(0,61,122,0.08) 20%, rgba(0,116,217,0.1) 40%, rgba(57,204,204,0.08) 60%, rgba(46,204,64,0.06) 80%, rgba(61,153,112,0.05) 100%)',
                                    borderColor: 'rgba(0,116,217,0.2)'
                                } : {
                                    backgroundColor: '#f9fafb',
                                    borderColor: '#e5e7eb'
                                }}
                            >
                                <div className="flex items-center">
                                    {meetingData.partnerPlaceId && (
                                        <span className="text-blue-500 text-sm drop-shadow-sm mr-1">✦</span>
                                    )}
                                    <span className="font-medium text-gray-900">{meetingData.placeName}</span>
                                </div>
                                <div className="text-sm text-gray-600 mb-1">{meetingData.placeAddress}</div>
                                {meetingData.placePhone && (
                                    <div className="text-sm text-gray-500">{meetingData.placePhone}</div>
                                )}
                            </div>
                        </div>
                    </div>

                    {/* 모임원 */}
                    <div>
                        <div className="flex items-center justify-between mb-4">
                            <h3 className="text-lg font-medium text-gray-900">밥친구</h3>
                            <div className="flex items-center text-gray-500">
                                <IoPeopleOutline size={17} className="mr-1" />
                                <span className="text-sm">
                                    {meetingData.memberCount}/{meetingData.maxCapacity}명
                                </span>
                            </div>
                        </div>

                        <div className="grid grid-cols-3 gap-3">
                            {meetingData.members.map((memberItem) => (
                                <div key={memberItem.id} className="text-center">
                                    <div className="relative w-16 h-16 bg-gray-200 rounded-full mx-auto mb-2 flex items-center justify-center">
                                        {memberItem.imageUrl ? (
                                            <img
                                                src={memberItem.imageUrl}
                                                alt={memberItem.name}
                                                className="w-full h-full rounded-full object-cover"
                                            />
                                        ) : (
                                            <IoPersonOutline size={24} className="text-gray-400" />
                                        )}
                                        {/* 포인트 표시 */}
                                        <div className="absolute -bottom-1 -right-1">
                                            <PointContainer point={memberItem.point} size={36} />
                                        </div>
                                    </div>
                                    <p className="text-sm text-gray-700 truncate">
                                        {memberItem.name}
                                        {memberItem.id === meetingData.hostId && (
                                            <span className="text-xs text-amber-600 ml-1">👑</span>
                                        )}
                                    </p>
                                </div>
                            ))}
                        </div>
                    </div>

                    <div className="p-4 bg-gray-100 rounded-md">
                        <div className="flex items-center justify-center mb-2">
                            <div className="flex items-center mr-4">
                                <span className="text-sm text-gray-700">최소 인원</span>
                                <span className="ml-2 text-lg font-semibold text-gray-900">{meetingData.minCapacity}명</span>
                            </div>
                            <div className="flex items-center ml-4">
                                <span className="text-sm text-gray-700">최대 인원</span>
                                <span className="ml-2 text-lg font-semibold text-gray-900">{meetingData.maxCapacity}명</span>
                            </div>
                        </div>
                        <ul className="list-disc pl-5 text-black text-sm">
                            <li>최소 인원을 충족하면 모임장이 모임을 확정할 수 있습니다.</li>
                            <li>모임 시간 15분 전 자동으로 모임이 확정됩니다. 만약 최소 인원이 충족되지 않으면 모임이 종료됩니다.</li>
                        </ul>
                    </div>

                    {/* 하단 여백 */}
                    <div className="h-20"></div>
                </div>

                {/* 하단 액션 바 */}
                <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 p-4">
                    <div className="max-w-md mx-auto flex items-center justify-between">
                        <div className="flex-1 pl-4">
                            {isHost ? (
                                <p className="text-xs text-blue-600">
                                    내가 모임장입니다
                                </p>
                            ) : isMember ? (
                                <p className="text-sm text-green-600">
                                    내가 참여하는 모임입니다
                                </p>
                            ) : meetingData.requiresApproval ? (
                                meetingData.isApplicationSubmitted ? (
                                    <p className="text-sm text-gray-600">
                                        승인 대기 중입니다
                                    </p>
                                ) : (
                                    <p className="text-sm text-amber-600">
                                        모임장의 승인이 필요합니다
                                    </p>
                                )
                            ) : (
                                <p className="text-sm text-blue-500">
                                    바로 참가 가능한 모임입니다
                                </p>
                            )}
                        </div>
                        <div className="flex space-x-2">
                            {showApplicationManagement && (
                                <button
                                    onClick={handleApplicationManagement}
                                    className="py-3 px-4 bg-blue-500 text-white rounded-lg font-medium active:bg-blue-600 transition-colors whitespace-nowrap"
                                >
                                    신청서
                                </button>
                            )}
                            {isHost && (
                                <button
                                    onClick={meetingData.status === 'RECRUITING' ? handleConfirmMeeting : undefined}
                                    disabled={meetingData.status === 'RECRUITING' ? !canConfirmMeeting : true}
                                    className={`flex-1 py-3 px-4 rounded-lg font-medium transition-colors ${
                                        meetingData.status === 'RECRUITING' && canConfirmMeeting
                                            ? 'bg-green-500 text-white active:bg-green-600'
                                            : 'bg-gray-300 text-gray-600 cursor-not-allowed'
                                    }`}
                                >
                                    {meetingData.status === 'RECRUITING' ? '모임확정' :
                                        meetingData.status === 'CONFIRMED' ? '확정완료' :
                                            meetingData.status === 'END' ? '모임종료' : '모임확정'}
                                </button>
                            )}
                            {(!isMember || !isFromChat) && (
                                <button
                                    onClick={handleActionButtonClick}
                                    className={`flex-1 py-3 px-4 rounded-lg font-medium transition-colors ${
                                        (isMember && !isFromChat) ? 'bg-amber-500 text-white active:bg-amber-600' :
                                            !isMember && (meetingData.status === 'CONFIRMED' || meetingData.status === 'END') ? 'bg-gray-300 text-gray-600 cursor-not-allowed' :
                                                !isMember && meetingData.requiresApproval && meetingData.isApplicationSubmitted ? 'bg-gray-300 text-gray-600 cursor-not-allowed' :
                                                    'bg-amber-500 text-white active:bg-amber-600'
                                    }`}
                                    disabled={!isMember && (meetingData.status === 'CONFIRMED' || meetingData.status === 'END' || (meetingData.requiresApproval && meetingData.isApplicationSubmitted))}
                                >
                                    {(() => {
                                        if (isMember && !isFromChat) return '채팅하기';
                                        if (!isMember) {
                                            if (meetingData.status === 'END') return '모임종료';
                                            if (meetingData.status === 'CONFIRMED') return '확정 완료';
                                            if (meetingData.requiresApproval && meetingData.isApplicationSubmitted) return '신청 완료';
                                            if (meetingData.requiresApproval) return '참가 신청';
                                            return '바로 참가';
                                        }
                                    })()}
                                </button>
                            )}
                        </div>
                    </div>
                </div>

                <OptionModal
                    isOpen={showSettingsModal}
                    onClose={() => setShowSettingsModal(false)}
                    title="모임 옵션"
                    options={getModalOptions()}
                />

                <SimpleInfoModal
                    isVisible={showPolicyModal}
                    onClose={handleClosePolicyModal}
                    title="제휴 매장 혜택"
                    content={partnerPlacePolicy}
                    maxWidth="max-w-md"
                    isLoading={policyLoading}
                />
            </div>
        </CommonLayout>
    );
};

export default MeetingPage;
